import _ from 'underscore';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDataSource } from '@bingads-webui-react/data-source';
import { AggregatedDataMap, AggregationMode, DataAggregationContextValue } from '../types';

const getPageKeysInOrder = (data: AggregatedDataMap) => Array.from(data.keys()).sort((a, b) => a - b);

const mergeAggregateData = (data: AggregatedDataMap) => {
  const pageKeysInOrder = getPageKeysInOrder(data);

  const mergedData = pageKeysInOrder.reduce((acc, key) => {
    const dataForPage = data.get(key);
    return acc.concat(dataForPage);
  }, []);

  return mergedData;
};

export const DataAggregationContext = createContext<DataAggregationContextValue | undefined>(undefined);

export const DataAggregation = ({ children }: { children: React.ReactNode }) => {
  const [lastSearchTerm, setLastSearchTerm] = useState<string>();
  const [dataMap, setDataMap] = useState<AggregatedDataMap>(new Map());

  const {
    searchTerm, currentPage, totalPages, items, isLoading, goToPage,
  } = useDataSource();

  useEffect(() => {
    if (isLoading || _.isEmpty(items)) { return; }

    const shouldResetAggregatedData = searchTerm !== lastSearchTerm;

    const newAggregatedData = new Map([...(shouldResetAggregatedData ? [] : dataMap)]);

    newAggregatedData.set(currentPage, items);

    setLastSearchTerm(searchTerm);
    setDataMap(newAggregatedData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, currentPage, items, isLoading]);

  const hasPagesMissing = useMemo(() => dataMap.size < totalPages, [dataMap, totalPages]);

  const getNextMissingPage = useCallback(() => {
    if (isLoading) { return; }

    // Pages are always fetched in order, and are 1-indexed. So, the next page to fetch is the size of the aggregated data + 1.
    const nextPageMissing = dataMap.size + 1;

    if (nextPageMissing > totalPages) { return; }

    goToPage(nextPageMissing);
  }, [dataMap.size, isLoading, totalPages, goToPage]);

  const getAggregatedData = useCallback((mode: AggregationMode) => {
    if (mode === 'page') {
      return dataMap.get(currentPage) ?? [];
    }

    return mergeAggregateData(dataMap);
  }, [dataMap, currentPage]);

  const value = useMemo(() => ({
    isLoading,
    hasPagesMissing,
    getNextMissingPage,
    getAggregatedData,
  }), [isLoading, hasPagesMissing, getNextMissingPage, getAggregatedData]);

  return (
    <DataAggregationContext.Provider value={value}>
      {children}
    </DataAggregationContext.Provider>
  );
};

export const useAggregatedData = () => {
  const context = useContext(DataAggregationContext);

  if (context === undefined) {
    throw new Error('useDataAggregation must be used within a DataSource provider');
  }

  return context;
};
